import { ROLE_LABELS } from "@eca/shared";
import { useQueryClient } from "@tanstack/react-query";
import { Building2, ChevronDown, LogOut, Menu as MenuIcon, PanelLeftClose, PanelLeftOpen, UserRound, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate, useParams } from "react-router";
import { Button } from "@/components/ui/button";
import { cn } from "@/components/ui/cn";
import { Menu } from "@/components/ui/menu";
import { authClient } from "@/lib/auth-client";
import { useMe } from "@/lib/session";
import { Footer } from "./auth-layout";
import { BackdropProvider } from "./backdrop";
import { Logo } from "./logo";
import { profession } from "@eca/shared";
import { useOffice } from "@/features/offices/hooks";
import { ADMIN_NAV, OFFICE_NAV, type NavItem } from "./nav";

// The current page gets a small vermilion bar at the left edge.
const NAV_ITEM = "relative mx-2 flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-ink-2 transition-[background,color,transform] hover:bg-brand-light/70 hover:text-brand";
// Top bar links: the current screen is underlined in vermilion.
const TOP_ITEM = "relative flex h-[60px] items-center px-2.5 text-[13px] font-bold whitespace-nowrap text-ink-2 transition-colors hover:text-brand after:absolute after:inset-x-2.5 after:bottom-0 after:h-[3px] after:rounded-t-sm after:bg-accent after:transition-transform after:scale-x-0";
const TOP_ACTIVE = "text-ink after:scale-x-100";

/** The office dashboard opens wide, with the side menu put away. */
const isDashboard = (path: string) => /^\/offices\/[^/]+\/?$/.test(path);

const NAV_ACTIVE = "bg-brand-light font-bold text-brand shadow-[0_1px_0_rgb(255_255_255/0.9)_inset] before:absolute before:top-1/2 before:left-1 before:h-5 before:w-1 before:-translate-y-1/2 before:rounded-full before:bg-accent";

/**
 * The motto in its own column along the left edge of the navigation band,
 * read top to bottom. The menu sits to its right, so the two never meet.
 */
function MottoRail() {
  return (
    <div aria-hidden className="w-2 shrink-0 bg-[linear-gradient(180deg,var(--color-brand),var(--color-accent))]" />
  );
}

export function AppLayout() {
  const { data: me } = useMe();
  const { officeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [drawer, setDrawer] = useState(false);
  // Wide screens: the side menu starts closed on the dashboard and open elsewhere;
  // the toggle in the top bar holds until the next screen.
  const [sidebar, setSidebar] = useState(() => !isDashboard(location.pathname));

  useEffect(() => {
    setDrawer(false);
    setSidebar(!isDashboard(location.pathname));
  }, [location.pathname]);
  // A 店舗 has no article screens: its menu shows only what its pack has.
  // (Read before the early return below, so the hooks run in the same order every time.)
  const office = me ? (me.offices.find((o) => o.id === officeId) ?? me.offices[0]) : undefined;
  const packQuery = useOffice(office?.id ?? "");
  if (!me) return null;

  const role = me.user.platformRole;
  const features = profession(packQuery.data?.profession ?? "tax_accountant").features;
  const shown = (n: NavItem) => n.roles.includes(role) && (!n.feature || features[n.feature]);

  const signOut = async () => {
    await authClient.signOut();
    qc.clear();
    navigate("/login", { replace: true });
  };

  const nav = (
    <nav aria-label="メインメニュー" className="space-y-6 py-4">
      {office && (
        <div>
          <p className="mb-1.5 truncate px-4 font-display text-[12px] font-bold text-muted">{office.name}</p>
          <ul>
            {OFFICE_NAV.filter(shown).map((n) => (
              <li key={n.path}>
                <NavLink
                  end={n.path === ""}
                  to={`/offices/${office.id}${n.path ? `/${n.path}` : ""}`}
                  className={({ isActive }) =>
                    cn(
                      NAV_ITEM,
                      isActive && NAV_ACTIVE,
                    )
                  }
                >
                  <n.icon className="size-[18px] shrink-0" />
                  {n.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div>
        <p className="mb-1.5 px-4 font-display text-[12px] font-bold text-muted">管理</p>
        <ul>
          {ADMIN_NAV.filter((n) => n.roles.includes(role)).map((n) => (
            <li key={n.path}>
              <NavLink
                to={n.path}
                className={({ isActive }) =>
                  cn(
                    NAV_ITEM,
                    isActive && NAV_ACTIVE,
                  )
                }
              >
                <n.icon className="size-[18px] shrink-0" />
                {n.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="sticky top-0 z-30 border-t-[3px] print:hidden border-b border-t-brand-dark border-b-line bg-surface/94 shadow-[0_8px_24px_-22px_rgb(16_30_45/0.55)] backdrop-blur">
        {/* 60px + the 3px and 1px rules = 64px, where the side menu and sticky bars begin. */}
        <div className="flex h-[60px] items-center gap-2 px-4">
          <button type="button" className="rounded-md p-1.5 text-ink-2 hover:bg-brand-light hover:text-brand lg:hidden" aria-label="メニューを開く" onClick={() => setDrawer(true)}>
            <MenuIcon className="size-6" />
          </button>
          <button
            type="button"
            className="hidden rounded-md p-1.5 text-ink-2 hover:bg-brand-light hover:text-brand lg:inline-flex"
            aria-label={sidebar ? "サイドメニューを閉じる" : "サイドメニューを開く"}
            aria-expanded={sidebar}
            aria-controls="side-menu"
            onClick={() => setSidebar((v) => !v)}
          >
            {sidebar ? <PanelLeftClose className="size-5" /> : <PanelLeftOpen className="size-5" />}
          </button>
          <div className="min-w-0 shrink">
            <Logo />
          </div>

          {office && (
            <nav aria-label="よく使う画面" className="ml-4 hidden items-center lg:flex">
              {OFFICE_NAV.filter((n) => n.header && shown(n)).map((n) => (
                <NavLink key={n.path} end={n.path === ""} to={`/offices/${office.id}${n.path ? `/${n.path}` : ""}`} className={({ isActive }) => cn(TOP_ITEM, isActive && TOP_ACTIVE)}>
                  {n.label}
                </NavLink>
              ))}
            </nav>
          )}

          <div className="ml-auto flex min-w-0 items-center gap-1 sm:gap-2">
            {me.offices.length > 0 && (
              <Menu
                label="事務所を切り替え"
                items={me.offices.map((o) => ({ label: o.name, icon: <Building2 />, onSelect: () => navigate(`/offices/${o.id}`) }))}
                trigger={
                  <Button variant="outline" size="sm" className="max-w-[44vw] min-w-0 sm:max-w-64 lg:max-w-44 xl:max-w-64">
                    <Building2 />
                    <span className="truncate">{office?.name ?? "事務所を選択"}</span>
                    <ChevronDown />
                  </Button>
                }
              />
            )}
            <Menu
              label={`${me.user.name}（${ROLE_LABELS[role]}）`}
              items={[
                { label: "アカウント設定", icon: <UserRound />, onSelect: () => navigate("/account") },
                "separator",
                { label: "ログアウト", icon: <LogOut />, onSelect: () => void signOut() },
              ]}
              trigger={
                <button type="button" className="flex shrink-0 items-center gap-2 rounded-md px-1.5 py-1 hover:bg-ground" aria-label="アカウントメニュー">
                  <span className="flex size-8 items-center justify-center rounded-full border border-brand-line bg-brand-light font-display text-sm font-bold text-brand">{me.user.name.slice(0, 1)}</span>
                  <span className="hidden text-left leading-tight md:block lg:hidden xl:block">
                    <span className="block max-w-36 truncate text-[13px] font-bold">{me.user.name}</span>
                    <span className="block text-[11px] text-muted">{ROLE_LABELS[role]}</span>
                  </span>
                  <ChevronDown className="hidden size-4 text-muted md:block lg:hidden xl:block" />
                </button>
              }
            />
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Fixed beside the work area: scrolling a page never moves the menu. */}
        {/* Closed, it slides out to the left and leaves the reading order. */}
        <aside
          id="side-menu"
          inert={!sidebar}
            className={cn(
            "fixed top-16 bottom-0 left-0 z-20 hidden w-60 border-r border-line bg-surface/96 shadow-[12px_0_32px_-30px_rgb(16_30_45/0.45)] backdrop-blur transition-[translate,visibility] duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)] lg:flex print:hidden",
            !sidebar && "invisible -translate-x-full",
          )}
        >
          <MottoRail />
          <div className="min-w-0 flex-1 overflow-y-auto overscroll-contain [scrollbar-width:thin] [scrollbar-color:rgb(255_255_255/0.2)_transparent]">{nav}</div>
        </aside>

        <AnimatePresence>
          {drawer && (
            <div className="fixed inset-0 z-40 lg:hidden">
              <motion.div className="absolute inset-0 bg-ink/50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setDrawer(false)} />
              <motion.aside
                className="absolute inset-y-0 left-0 w-[min(18rem,85vw)] overflow-y-auto border-r border-line bg-surface shadow-xl"
                initial={{ x: "-100%" }}
                animate={{ x: 0 }}
                exit={{ x: "-100%" }}
                transition={{ type: "tween", duration: 0.22 }}
              >
                <div className="flex items-center justify-between border-b border-line px-4 py-3">
                  <span className="text-sm font-bold text-ink">メニュー</span>
                  <button type="button" onClick={() => setDrawer(false)} className="rounded p-1 text-ink-2 hover:bg-brand-light hover:text-brand" aria-label="メニューを閉じる">
                    <X className="size-5" />
                  </button>
                </div>
                {nav}
              </motion.aside>
            </div>
          )}
        </AnimatePresence>

        {/* workspace-column: the size container a page's header and bands reach across. */}
        <div className={cn("workspace-column flex min-w-0 flex-1 flex-col overflow-x-clip transition-[padding] duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)] print:pl-0", sidebar && "lg:pl-60")}>
          <main className="mx-auto w-full max-w-[1440px] flex-1 p-4 sm:p-6 lg:p-8 print:max-w-none print:p-0">
            <BackdropProvider>
              <Outlet />
            </BackdropProvider>
          </main>
          <div className="print:hidden">
            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
}
